
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { cloneDeep } from 'lodash';
import { PERMISSION } from '@/permissions';
import IconButton from '@/components/shared/suites/IconButton.vue';
import ItemDiscountModal from '../modals/ItemDiscountModal.vue';

const namespace = 'orders';

@Component<OrderCartItem>({
	components: {
		IconButton,
		ItemDiscountModal
	}
})
export default class OrderCartItem extends Vue {
	@Action('setSelectedItem', { namespace }) setSelectedItem!: (payload: { selectedItem: MenuItem; sectionSlug?: string }) => void;
	@Action('setOrderItem', { namespace }) setOrderItem!: (item: MenuOrderItem) => void;
	@Action('setEditingItem', { namespace }) setEditingItem!: (editing: boolean) => void;
	@Getter('getPublishedMenus', { namespace: 'menus' }) private publishedMenus!: Menu[];
	@Getter('getMenus', { namespace: 'menus' }) private allMenus!: Menu[];
	@Getter('isCateringRestaurant', { namespace: 'auth' }) private isCateringRestaurant!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getOrderType', { namespace: 'orders' }) private orderType!: string;
	@Prop({ type: Object, required: true }) private item!: MenuOrderItem;

	private itemDiscountModalOpen: boolean = false;

	private get isPriceOnRequest(): boolean {
		return this.item.price_on_request && this.isCateringRestaurant && this.orderType === 'generic-catering';
	}

	private get canApplyItemDiscount(): boolean {
		return this.hasPermission(PERMISSION.ORDER_ITEM_LEVEL_DISCOUNT) && this.isCateringRestaurant && this.orderType === 'generic-catering' && !this.item.alcoholic_beverage;
	}

	/**
	 * Find the item to edit and set it as the selected item
	 *
	 * @return {void}
	 */
	private setItemToEdit(): void {
		const menu = this.publishedMenus.find((menu: Menu) => menu.id === this.item.menu_id)
			|| (this.item.openItem ? this.allMenus.find((menu: Menu) => menu.id === this.item.menu_id) : null);
		let menuItem: MenuItem | null = null;
		if (menu?.sections?.length) {
			menuItem = menu.sections
				.map((section: MenuSection) => section.items)
				.flat()
				.find((menuItem: MenuItem) => menuItem.id === this.item.id);
		}
		else if (menu?.items?.length) {
			menuItem = menu.items.find((menuItem: MenuItem) => menuItem.id === this.item.id) || null;
		}

		if (menuItem) {
			this.setEditingItem(true);
			this.setSelectedItem({ selectedItem: menuItem, sectionSlug: this.item.sectionSlug });
			this.setOrderItem(cloneDeep(this.item));
		}
	}

	/**
	 * Determine if an option should be displayed
	 * If the option is not a default selection, it should be displayed if it has a quantity
	 * If the option is a default selection, it should be displayed if it has suboptions or a quantity > 1
	 *
	 * @param {OrderOption} option
	 * @return {boolean}
	 */
	private displayOption(option: OrderOption): boolean {
		const optionHasSelectedSubOptions = option.options?.some(subOptionGroup => subOptionGroup.values.some(subOption => subOption.quantity > 0));
		return (!option.default_selection && option.quantity > 0) || !!(option.default_selection && (optionHasSelectedSubOptions || option.quantity > 1));
	}
}
